import { ChangeEvent, useMemo, useState } from "react";
import StorageIcon from "@mui/icons-material/Storage";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Tooltip
} from "@mui/material";
import { useFormikContext } from "formik";
import type { RuntimeConfig } from "../pages/ConfigPage";

const UDP_PORT_MIN = 1024;
const UDP_PORT_MAX = 65535;

type WebRtcFormProps = {
  isDesktopShell?: boolean;
};

const WebRtcForm = ({ isDesktopShell = false }: WebRtcFormProps) => {
  const formik = useFormikContext<RuntimeConfig>();
  const [iceServersDialogOpen, setIceServersDialogOpen] = useState(false);
  const [iceServersDraft, setIceServersDraft] = useState("");
  const [iceServersInitialDraft, setIceServersInitialDraft] = useState("");
  const [iceServersValid, setIceServersValid] = useState(true);

  const hasTurnServer = useMemo(() => {
    try {
      const parsed = JSON.parse(formik.values.iceServersJson);
      if (!Array.isArray(parsed)) {
        return false;
      }

      return parsed.some((entry) => {
        if (typeof entry !== "object" || entry === null) {
          return false;
        }

        const urls = (entry as { urls?: unknown }).urls;
        if (typeof urls === "string") {
          const normalized = urls.trim().toLowerCase();
          return normalized.startsWith("turn:") || normalized.startsWith("turns:");
        }

        if (Array.isArray(urls)) {
          return urls.some((url) => {
            if (typeof url !== "string") {
              return false;
            }
            const normalized = url.trim().toLowerCase();
            return normalized.startsWith("turn:") || normalized.startsWith("turns:");
          });
        }

        return false;
      });
    } catch {
      return false;
    }
  }, [formik.values.iceServersJson]);

  const portRangeErrors = formik.errors.ephemeralUdpPortRange as Partial<Record<"min" | "max", string>> | undefined;
  const portRangeTouched = formik.touched.ephemeralUdpPortRange as Partial<Record<"min" | "max", boolean>> | undefined;
  const getPortRangeError = (field: "min" | "max") => {
    if (!formik.values.ephemeralUdpPortRangeEnabled) {
      return undefined;
    }
    if (!portRangeTouched?.[field] && formik.submitCount === 0) {
      return undefined;
    }
    return portRangeErrors?.[field];
  };
  const formatPortValue = (value: number) => (value === 0 ? "" : value);
  const handlePortRangeChange = (field: "min" | "max") => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const parsed = Number.parseInt(event.target.value, 10);
    void formik.setFieldValue(`ephemeralUdpPortRange.${field}`, Number.isNaN(parsed) ? 0 : parsed);
  };

  const prettyPrintIceServers = (value: string) => {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return JSON.stringify(parsed, null, 2);
      }
    } catch {
      // Ignore parsing errors and fall back to the original value.
    }

    return value;
  };

  const handleIceServersToggle = () => {
    const prettyValue = prettyPrintIceServers(formik.values.iceServersJson);
    setIceServersInitialDraft(prettyValue);
    setIceServersDraft(prettyValue);
    try {
      const parsed = JSON.parse(prettyValue);
      setIceServersValid(Array.isArray(parsed));
    } catch {
      setIceServersValid(false);
    }
    setIceServersDialogOpen(true);
  };

  const handleIceServersDraftChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.target;
    setIceServersDraft(value);
    try {
      const parsed = JSON.parse(value);
      setIceServersValid(Array.isArray(parsed));
    } catch {
      setIceServersValid(false);
    }
  };

  const handleIceServersCancel = () => {
    setIceServersDraft(iceServersInitialDraft);
    setIceServersValid(true);
    setIceServersDialogOpen(false);
  };

  const handleIceServersApply = () => {
    try {
      const parsed = JSON.parse(iceServersDraft);
      if (!Array.isArray(parsed)) {
        setIceServersValid(false);
        return;
      }

      const formatted = JSON.stringify(parsed, null, 2);
      void formik.setFieldValue("iceServersJson", formatted, true);
      formik.setFieldTouched("iceServersJson", true, false);
      setIceServersInitialDraft(formatted);
      setIceServersDraft(formatted);
      setIceServersValid(true);
      setIceServersDialogOpen(false);
    } catch {
      setIceServersValid(false);
    }
  };

  const iceServersOkEnabled = iceServersValid && iceServersDraft !== iceServersInitialDraft;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
      {!isDesktopShell && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Comma separated ICE servers or JSON describing STUN/TURN servers">
              <Button
                variant="outlined"
                startIcon={<StorageIcon fontSize="small" />}
                onClick={handleIceServersToggle}
              >
                ICE servers
              </Button>
            </Tooltip>
          </Box>
          <Dialog
            open={iceServersDialogOpen}
            onClose={handleIceServersCancel}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <DialogTitle>ICE servers</DialogTitle>
            <DialogContent>
              <TextField
                label="ICE servers JSON"
                value={iceServersDraft}
                onChange={handleIceServersDraftChange}
                multiline
                minRows={8}
                error={!iceServersValid}
                helperText={!iceServersValid ? "Provide a valid JSON array" : " "}
                slotProps={{ input: { sx: { fontFamily: "monospace" } } }}
                sx={{ width: { xs: "100%", sm: "60ch" }, mt: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleIceServersCancel}>Cancel</Button>
              <Button variant="contained" onClick={handleIceServersApply} disabled={!iceServersOkEnabled}>
                Ok
              </Button>
            </DialogActions>
          </Dialog>
          <Tooltip title="Include a loopback ICE candidate for local testing">
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.loopbackIceCandidate}
                  onChange={(_, checked) => formik.setFieldValue("loopbackIceCandidate", checked)}
                  name="loopbackIceCandidate"
                />
              }
              label="Loopback ICE candidate"
            />
          </Tooltip>
          <Tooltip title="Keep host network candidates when gathering ICE addresses">
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.keepHostCandidates}
                  onChange={(_, checked) => formik.setFieldValue("keepHostCandidates", checked)}
                  name="keepHostCandidates"
                />
              }
              label="Keep host candidates"
            />
          </Tooltip>
          <Tooltip title="Use the full ICE candidate gathering strategy">
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.fullIce}
                  onChange={(_, checked) => formik.setFieldValue("fullIce", checked)}
                  name="fullIce"
                />
              }
              label="Use full ICE"
            />
          </Tooltip>
          <Tooltip
            title={
              hasTurnServer
                ? "Force all traffic through relay servers"
                : "Add a TURN server to enable this option"
            }
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.forceRelay}
                  onChange={(_, checked) => formik.setFieldValue("forceRelay", checked)}
                  name="forceRelay"
                  disabled={!hasTurnServer}
                />
              }
              label="Force relay"
            />
          </Tooltip>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
              width: "100%"
            }}
          >
            <Tooltip title="Advertise a 1:1 NAT mapping for clients">
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.nat1to1Enabled}
                    onChange={(_, checked) => formik.setFieldValue("nat1to1Enabled", checked)}
                    name="nat1to1Enabled"
                  />
                }
                label="Enable 1:1 NAT mapping"
                sx={{ mb: 0, width: { xs: "auto", sm: 220 }, flexShrink: 0 }}
                slotProps={{ typography: { sx: { whiteSpace: "nowrap" } } }}
              />
            </Tooltip>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
              <Tooltip title="Advertised public IP address shared with peers">
                <TextField
                  label="Public IP"
                  name="publicIp"
                  value={formik.values.publicIp}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  size="small"
                  disabled={!formik.values.nat1to1Enabled}
                  inputProps={{ "aria-hidden": !formik.values.nat1to1Enabled }}
                  error={Boolean(formik.touched.publicIp && formik.errors.publicIp)}
                  helperText={(formik.touched.publicIp && (formik.errors.publicIp as string)) || " "}
                  sx={{
                    width: { xs: "100%", sm: "232px" },
                    maxWidth: "100%",
                    transition: "opacity 150ms",
                    opacity: formik.values.nat1to1Enabled ? 1 : 0.5,
                    visibility: "visible",
                    "& .MuiFormHelperText-root": {
                      position: "absolute",
                      bottom: "-20px"
                    }
                  }}
                />
              </Tooltip>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
              width: "100%"
            }}
          >
            <Tooltip title="Restrict the UDP ports used for host and srflx ICE candidates">
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.ephemeralUdpPortRangeEnabled}
                    onChange={(_, checked) => formik.setFieldValue("ephemeralUdpPortRangeEnabled", checked)}
                    name="ephemeralUdpPortRangeEnabled"
                  />
                }
                label="Ephemeral UDP ports"
                sx={{ mb: 0, width: { xs: "auto", sm: 220 }, flexShrink: 0 }}
                slotProps={{ typography: { sx: { whiteSpace: "nowrap" } } }}
              />
            </Tooltip>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
              <Tooltip title="Lowest UDP port to advertise when enabled">
                <TextField
                  label="Min"
                  name="ephemeralUdpPortRange.min"
                  type="number"
                  value={formatPortValue(formik.values.ephemeralUdpPortRange.min)}
                  onChange={handlePortRangeChange("min")}
                  onBlur={formik.handleBlur}
                  size="small"
                  disabled={!formik.values.ephemeralUdpPortRangeEnabled}
                  inputProps={{ min: UDP_PORT_MIN, max: UDP_PORT_MAX }}
                  error={Boolean(getPortRangeError("min"))}
                  helperText={getPortRangeError("min") || " "}
                  sx={{
                    width: { xs: "100%", sm: "110px" },
                    maxWidth: { xs: "100%", sm: "110px" },
                    transition: "opacity 150ms",
                    opacity: formik.values.ephemeralUdpPortRangeEnabled ? 1 : 0.5
                  }}
                />
              </Tooltip>
              <Tooltip title="Highest UDP port to advertise when enabled">
                <TextField
                  label="Max"
                  name="ephemeralUdpPortRange.max"
                  type="number"
                  value={formatPortValue(formik.values.ephemeralUdpPortRange.max)}
                  onChange={handlePortRangeChange("max")}
                  onBlur={formik.handleBlur}
                  size="small"
                  disabled={!formik.values.ephemeralUdpPortRangeEnabled}
                  inputProps={{ min: UDP_PORT_MIN, max: UDP_PORT_MAX }}
                  error={Boolean(getPortRangeError("max"))}
                  helperText={getPortRangeError("max") || " "}
                  sx={{
                    width: { xs: "100%", sm: "110px" },
                    maxWidth: { xs: "100%", sm: "110px" },
                    transition: "opacity 150ms",
                    opacity: formik.values.ephemeralUdpPortRangeEnabled ? 1 : 0.5
                  }}
                />
              </Tooltip>
            </Box>
          </Box>
        </>
      )}
      <TextField select label="Playback acceleration" name="playbackAcceleration"
        value={formik.values.playbackAcceleration} onChange={formik.handleChange}
        helperText="Auto prefers supported hardware and falls back to software. Changes apply when each stream next starts."
        sx={{ maxWidth: 560 }}>
        <MenuItem value="auto">Auto</MenuItem>
        <MenuItem value="software">Software</MenuItem>
      </TextField>
      <Tooltip title="If a Passthrough is selected and the source video codec is H265, transcode it to H264, so it could be decoded by the browser">
        <FormControlLabel
          control={
            <Switch
              checked={formik.values.alwaysTranscodeH265ToH264}
              onChange={(_, checked) => formik.setFieldValue("alwaysTranscodeH265ToH264", checked)}
              name="alwaysTranscodeH265ToH264"
            />
          }
          label="Always transcode H265 to H264"
        />
      </Tooltip>
      <Tooltip title="Expose the WebRtc debug helper page in the UI">
        <FormControlLabel
          control={
            <Switch
              checked={formik.values.enableWebRtcDebug}
              onChange={(_, checked) => formik.setFieldValue("enableWebRtcDebug", checked)}
              name="enableWebRtcDebug"
            />
          }
          label="Enable WebRtc debug page"
        />
      </Tooltip>
    </Box>
  );
};

export default WebRtcForm;
